import { Menu } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { Sidebar } from "@/components/layout/Sidebar";
import { useInboxStream } from "@/features/inbox/useInboxStream";
import type { NavigationSection, SessionUser } from "@/lib/api/types";
import { cn } from "@/lib/utils";

interface AppShellProps {
  children: ReactNode;
  user: SessionUser;
  sections: NavigationSection[];
  /**
   * `false` para las pantallas rediseñadas: cada una pone su propio padding
   * (y las de alto completo, ninguno). Las viejas siguen con el `p-6` de antes.
   */
  padded?: boolean;
}

/**
 * Sidebar + contenido. **No hay barra superior**: cada pantalla es dueña de su
 * cabecera (`PageHeader`).
 *
 * Tanto el usuario como el menú llegan resueltos desde la API: un módulo que
 * este rol no puede ver no viaja al cliente.
 *
 * En pantallas chicas el sidebar se despliega sobre el contenido desde un
 * botón flotante, y se cierra con Esc, tocando afuera o al navegar.
 */
export function AppShell({ children, user, sections, padded = true }: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  // La bandeja en vivo: una conexión por pestaña, abierta mientras haya sesión.
  useInboxStream();

  useEffect(() => {
    if (!menuOpen) return;
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [menuOpen]);

  return (
    <div className="bg-background flex h-screen overflow-hidden">
      <Sidebar
        sections={sections}
        user={user}
        mobileOpen={menuOpen}
        onMobileClose={() => setMenuOpen(false)}
      />
      {menuOpen && (
        <div
          aria-hidden
          onClick={() => setMenuOpen(false)}
          className="bg-scrim animate-fade-in fixed inset-0 z-40 lg:hidden"
        />
      )}

      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="Abrir menú"
        aria-expanded={menuOpen}
        className="bg-surface border-border text-foreground rounded-chip fixed top-3 left-3 z-30 grid size-10 place-items-center border lg:hidden"
      >
        <Menu aria-hidden strokeWidth={1.75} className="size-[18px]" />
      </button>

      {/* Las pantallas viejas todavía dependen del `p-6`; las rediseñadas lo
          apagan desde su ruta (`handle.ownsPadding`) y ponen el suyo. */}
      <main
        className={cn(
          "relative min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto",
          padded && "p-6 pt-16 lg:pt-6",
        )}
      >
        {children}
      </main>
    </div>
  );
}
